import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useCachedTVL } from '../hooks/useCachedTVL';
import { formatNumber } from '../utils/formatNumber';
import { useAllTokens } from '../hooks/useAllTokens';
import { useAccount } from 'wagmi';
import QuickLockModal from '../components/shared/modals/QuickLockModal';
import QuickVestModal from '../components/shared/modals/QuickVestModal';
import { PageShell } from '../components/shared/PageShell';

// ==================== STYLED COMPONENTS ====================

const Header = styled.div`
  margin-bottom: 48px;
  
  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 300;
  color: #ffffff;
  margin-bottom: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: -0.02em;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Plus Jakarta Sans', sans-serif;
  margin: 0;
`;

// ==================== TOTAL TVL SECTION ====================

const TotalTVLContainer = styled.div`
  background: #000000;
  border: 1px solid #333333;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  margin-bottom: 32px;
  backdrop-filter: blur(12px);
  box-shadow: 0 0 30px #000000;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.8), transparent);
  }

  &:hover {
    background: #000000;
    box-shadow: 0 0 50px #333333;
    border-color: #444444;
  }
  
  @media (max-width: 768px) {
    padding: 32px 20px;
  }
`;

const TotalTVLLabel = styled.div`
  font-size: 13px;
  color: var(--color-neon-purple);
  margin-bottom: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
`;

const TotalTVLValue = styled.div`
  font-size: 64px;
  font-weight: 300;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: -0.03em;
  margin-bottom: 12px;
  text-shadow: 0 0 30px rgba(168, 85, 247, 0.6);
  
  @media (max-width: 768px) {
    font-size: 48px;
  }
`;

const TotalTVLDescription = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
`;

// ==================== BREAKDOWN CARDS ====================

const BreakdownContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 48px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const BreakdownCard = styled.div<{ $isExpanded?: boolean }>`
  background: #000000;
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(12px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.6), transparent);
  }
  
  &:hover {
    background: #000000;
    box-shadow: 0 0 30px rgba(0, 212, 255, 0.15);
    border-color: rgba(0, 212, 255, 0.4);
  }
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  cursor: pointer;
  user-select: none;
`;

const CardTitle = styled.div`
  font-size: 13px;
  color: var(--color-neon-blue);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpandIcon = styled.div<{ $isExpanded: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000000;
  border-radius: 6px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  &:hover {
    background: #333333;
  }
`;

const CardValue = styled.div`
  font-size: 42px;
  font-weight: 300;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: -0.02em;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const CardDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
  margin-bottom: 16px;
`;

const ExpandedContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '200px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.$isExpanded ? '1' : '0'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ExplorerLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 12px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

// ==================== TABLE SECTIONS ====================

const TablesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const TableSection = styled.div`
  background: #000000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(12px);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);
  }
`;

const TableTitle = styled.h2`
  font-size: 20px;
  font-weight: 300;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  margin-bottom: 20px;
  letter-spacing: -0.01em;
`;

const Table = styled.div`
  width: 100%;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 100px 100px;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(168, 85, 247, 0.05);
  border-radius: 10px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    grid-template-columns: 40px 40px 1fr 90px;
    gap: 8px;
  }
`;

const TableHeaderCell = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  
  @media (max-width: 768px) {
    font-size: 9px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 100px 100px;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(42, 31, 61, 0.3);
  border-radius: 10px;
  margin-bottom: 6px;
  align-items: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(42, 31, 61, 0.5);
    transform: translateX(4px);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 30px 32px 1fr 70px 70px;
    gap: 6px;
    padding: 12px 8px;
  }
`;

const RankCell = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--color-neon-purple);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-shadow: 0 0 8px rgba(188, 19, 254, 0.4);
`;

const LogoCell = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #333333;
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.4) 0%, #333333 100%);
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const TokenInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TokenNameText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const TokenSymbolText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--color-neon-blue);
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const AmountCell = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const ValueCell = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--color-neon-green);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-shadow: 0 0 10px rgba(10, 255, 10, 0.3);
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

// ==================== LOADING & EMPTY STATES ====================

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #333333;
  border-top-color: #a855f7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0;
  font-weight: 500;
`;

// ==================== REFRESHING INDICATOR ====================

const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const RefreshingBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid #333333;
  border-radius: 20px;
  font-size: 12px;
  color: rgba(168, 85, 247, 1);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
  animation: ${pulseAnimation} 1.5s ease-in-out infinite;
  margin-left: 12px;
`;

const RefreshingDot = styled.div`
  width: 6px;
  height: 6px;
  background: rgba(168, 85, 247, 1);
  border-radius: 50%;
`;

const SkeletonPulse = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonValue = styled.div`
  height: 64px;
  width: 200px;
  margin: 0 auto 12px;
  background: linear-gradient(90deg, #000000 25%, #333333 50%, #000000 75%);
  background-size: 200% 100%;
  animation: ${SkeletonPulse} 1.5s ease-in-out infinite;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    height: 48px;
    width: 150px;
  }
`;

const SkeletonCardValue = styled.div`
  height: 42px;
  width: 120px;
  background: linear-gradient(90deg, #000000 25%, #333333 50%, #000000 75%);
  background-size: 200% 100%;
  animation: ${SkeletonPulse} 1.5s ease-in-out infinite;
  border-radius: 8px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    height: 36px;
    width: 100px;
  }
`;

// ==================== ACTION BUTTONS ====================

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 48px;
  
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 32px;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(168, 85, 247, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(80, 80, 90, 0.4);
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
  }
`;

// ==================== HELPER FUNCTIONS ====================

const formatUSDValue = (value: number): React.ReactNode => {
  if (value === 0) return '$0.00';
  if (value < 0.01) {
    const valueStr = value.toFixed(10);
    const match = valueStr.match(/^0\.0*([1-9])/);
    if (match) {
      const zeros = valueStr.indexOf(match[1]) - 2;
      const significantDigits = valueStr.slice(valueStr.indexOf(match[1]), valueStr.indexOf(match[1]) + 2);
      return (
        <>
          $0.0<sub>{zeros}</sub>{significantDigits}
        </>
      );
    }
  }
  return `$${formatNumber(value, 2)}`;
};

const formatAmount = (amount: number): string => {
  if (amount >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(2)}B`;
  }
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(2)}K`;
  }
  return amount.toFixed(2);
};

// ==================== MAIN COMPONENT ====================

const TokenLocksPage: React.FC = () => {
  // Use cached TVL hook for instant loading with background refresh
  const {
    totalEcosystemTVL,
    tokenLocksTVL,
    nftPositionsTVL,
    vestedTVL,
    tokenCount,
    locks,
    vestedTokens,
    isLoadingLocks,
    isLoadingNFT,
    isLoadingVested,
    isRefreshing,
    hasCachedData,
    error
  } = useCachedTVL();
  
  const { allTokens } = useAllTokens();
  const { isConnected } = useAccount();

  const [liquidityExpanded, setLiquidityExpanded] = useState(false);
  const [tokenLocksExpanded, setTokenLocksExpanded] = useState(false);
  const [vestedExpanded, setVestedExpanded] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const [showVest, setShowVest] = useState(false);

  // Helper function to check if a token has a valid logo
  const hasValidLogo = (token: any) => {
    return token.logo_url &&
      token.logo_url !== '/default-token.png' &&
      !token.logo_url.includes('default-token.png');
  };

  // Filter tokens with logos (Mavrk-deployed)
  const mavrkLocks = locks.filter(hasValidLogo).sort((a, b) => b.usdValue - a.usdValue);
  const mavrkVested = vestedTokens.filter(hasValidLogo).sort((a, b) => b.usdValue - a.usdValue);

  // Only show full loading state if we have no cached data at all
  const showFullLoading = isLoadingLocks && !hasCachedData && locks.length === 0;

  if (showFullLoading) {
    return (
      <PageShell>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </PageShell>
    );
  }

  if (error && !hasCachedData) {
    return (
      <PageShell>
        <LoadingContainer>
          Error loading locks: {error}
        </LoadingContainer>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title>Locks & TVL</Title>
          {isRefreshing && hasCachedData && (
            <RefreshingBadge>
              <RefreshingDot />
              Updating
            </RefreshingBadge>
          )}
        </div>
        <Subtitle>
          Analytics Dashboard for Mavrk Ecosystem TVL
        </Subtitle>
      </Header>

      {/* Total Ecosystem TVL - DeFiLlama Style */}
      <TotalTVLContainer>
        <TotalTVLLabel>Mavrk Ecosystem TVL</TotalTVLLabel>
        {totalEcosystemTVL > 0 ? (
          <TotalTVLValue>
            ${formatNumber(totalEcosystemTVL, 2)}
          </TotalTVLValue>
        ) : (
          <SkeletonValue />
        )}
        <TotalTVLDescription>
          Total Value Locked Across All Contracts and Positions
        </TotalTVLDescription>
      </TotalTVLContainer>

      {/* Breakdown Cards with Expandable Explorer Links */}
      <BreakdownContainer>
        {/* Liquidity Locker */}
        <BreakdownCard>
          <CardTitleRow onClick={() => setLiquidityExpanded(!liquidityExpanded)}>
            <CardTitle>
              Liquidity Locker
            </CardTitle>
            <ExpandIcon $isExpanded={liquidityExpanded}>
              <i className="fas fa-chevron-down" style={{ fontSize: '12px', color: 'rgba(168, 85, 247, 1)' }}></i>
            </ExpandIcon>
          </CardTitleRow>
          {nftPositionsTVL > 0 ? (
            <CardValue>
              ${formatNumber(nftPositionsTVL, 2)}
            </CardValue>
          ) : isLoadingNFT ? (
            <SkeletonCardValue />
          ) : (
            <CardValue>$0.00</CardValue>
          )}
          {tokenCount > 0 && (
            <CardDescription>
              Total Value Locked Across {tokenCount} Positions
            </CardDescription>
          )}
          <ExpandedContent $isExpanded={liquidityExpanded}>
            <ExplorerLink
              href="https://explorer.inkonchain.com/address/0x0f417FaB5b78aaCC265137Ebd83b41c44f515d3F?tab=tokens_nfts"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-external-link-alt" style={{ fontSize: '12px' }}></i>
              View Liquidity Locker Information
            </ExplorerLink>
          </ExpandedContent>
        </BreakdownCard>

        {/* Token Locks */}
        <BreakdownCard>
          <CardTitleRow onClick={() => setTokenLocksExpanded(!tokenLocksExpanded)}>
            <CardTitle>
              Token Locks
            </CardTitle>
            <ExpandIcon $isExpanded={tokenLocksExpanded}>
              <i className="fas fa-chevron-down" style={{ fontSize: '12px', color: 'rgba(168, 85, 247, 1)' }}></i>
            </ExpandIcon>
          </CardTitleRow>
          {tokenLocksTVL > 0 ? (
            <CardValue>
              ${formatNumber(tokenLocksTVL, 2)}
            </CardValue>
          ) : isLoadingLocks ? (
            <SkeletonCardValue />
          ) : (
            <CardValue>$0.00</CardValue>
          )}
          {locks.length > 0 && (
            <CardDescription>
              Total Value of Locked Tokens
            </CardDescription>
          )}
          <ExpandedContent $isExpanded={tokenLocksExpanded}>
            <ExplorerLink
              href="https://explorer.inkonchain.com/address/0xd2C5947A6777500D9b7ad55a4D48AeF855AE6aBA?tab=tokens"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-external-link-alt" style={{ fontSize: '12px' }}></i>
              View Token Locks Information
            </ExplorerLink>
          </ExpandedContent>
        </BreakdownCard>

        {/* Vested Tokens */}
        <BreakdownCard>
          <CardTitleRow onClick={() => setVestedExpanded(!vestedExpanded)}>
            <CardTitle>
              Vested Tokens
            </CardTitle>
            <ExpandIcon $isExpanded={vestedExpanded}>
              <i className="fas fa-chevron-down" style={{ fontSize: '12px', color: 'rgba(168, 85, 247, 1)' }}></i>
            </ExpandIcon>
          </CardTitleRow>
          {vestedTVL > 0 ? (
            <CardValue>
              ${formatNumber(vestedTVL, 2)}
            </CardValue>
          ) : isLoadingVested ? (
            <SkeletonCardValue />
          ) : (
            <CardValue>$0.00</CardValue>
          )}
          {vestedTokens.length > 0 && (
            <CardDescription>
              Total Value of Vested Tokens
            </CardDescription>
          )}
          <ExpandedContent $isExpanded={vestedExpanded}>
            <ExplorerLink
              href="https://explorer.inkonchain.com/address/0x9496Ff7A7BE0A91F582Baa96ac12a0A36300750c?tab=tokens"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-external-link-alt" style={{ fontSize: '12px' }}></i>
              View Vested Tokens Information
            </ExplorerLink>
          </ExpandedContent>
        </BreakdownCard>
      </BreakdownContainer>

      {/* Lock and Vest Action Buttons */}
      <ActionButtonsContainer>
        <ActionButton onClick={() => setShowLock(true)} disabled={!isConnected}>
          Lock Tokens
        </ActionButton>
        <ActionButton onClick={() => setShowVest(true)} disabled={!isConnected}>
          Vest Tokens
        </ActionButton>
      </ActionButtonsContainer>

      {/* Table Sections - DeFiLlama Style */}
      <TablesContainer>
        {/* Token Locks Table */}
        <TableSection>
          <TableTitle>Token Locks</TableTitle>
          {mavrkLocks.length === 0 ? (
            <EmptyState>
              <EmptyIcon>🔒</EmptyIcon>
              <EmptyText>No tokens currently locked</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableHeaderCell>#</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
                <TableHeaderCell>Token</TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>Amount</TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>Value</TableHeaderCell>
              </TableHeader>
              {mavrkLocks.map((lock, index) => (
                <TableRow key={`${lock.tokenAddress}-${index}`}>
                  <RankCell>{index + 1}</RankCell>
                  <LogoCell
                    src={lock.logo_url || '/default-token.png'}
                    alt={lock.symbol}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/default-token.png';
                    }}
                  />
                  <TokenInfoCell>
                    <TokenNameText>{lock.name || 'Unknown Token'}</TokenNameText>
                    <TokenSymbolText>${lock.symbol || '???'}</TokenSymbolText>
                  </TokenInfoCell>
                  <AmountCell>
                    {formatAmount(lock.tokenAmount)} {lock.symbol}
                  </AmountCell>
                  <ValueCell>
                    {formatUSDValue(lock.usdValue)}
                  </ValueCell>
                </TableRow>
              ))}
            </Table>
          )}
        </TableSection>

        {/* Vested Tokens Table */}
        <TableSection>
          <TableTitle>Vested Tokens</TableTitle>
          {mavrkVested.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📊</EmptyIcon>
              <EmptyText>No tokens currently vested</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableHeaderCell>#</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
                <TableHeaderCell>Token</TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>Amount</TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>Value</TableHeaderCell>
              </TableHeader>
              {mavrkVested.map((vested, index) => (
                <TableRow key={`${vested.tokenAddress}-${index}`}>
                  <RankCell>{index + 1}</RankCell>
                  <LogoCell
                    src={vested.logo_url || '/default-token.png'}
                    alt={vested.symbol}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/default-token.png';
                    }}
                  />
                  <TokenInfoCell>
                    <TokenNameText>{vested.name || 'Unknown Token'}</TokenNameText>
                    <TokenSymbolText>${vested.symbol || '???'}</TokenSymbolText>
                  </TokenInfoCell>
                  <AmountCell>
                    {formatAmount(vested.tokenAmount)} {vested.symbol}
                  </AmountCell>
                  <ValueCell>
                    {formatUSDValue(vested.usdValue)}
                  </ValueCell>
                </TableRow>
              ))}
            </Table>
          )}
        </TableSection>
      </TablesContainer>

      {/* Lock Token Modal */}
      <QuickLockModal
        isOpen={showLock}
        onClose={() => setShowLock(false)}
        userTokens={allTokens.map(t => ({
          contract_address: t.contract_address || '',
          name: t.name || '',
          symbol: t.symbol || '',
          logo_url: t.logo_url || '',
          network: 'ink',
          deployer_address: t.deployer_address || '',
          dex: t.dex
        }))}
        onLockCreated={() => {
          console.log('Lock created successfully');
        }}
      />

      {/* Vest Token Modal */}
      <QuickVestModal
        isOpen={showVest}
        onClose={() => setShowVest(false)}
        userTokens={allTokens.map(t => ({
          contract_address: t.contract_address || '',
          name: t.name || '',
          symbol: t.symbol || '',
          logo_url: t.logo_url || '',
          network: 'ink',
          deployer_address: t.deployer_address || '',
          dex: t.dex
        }))}
        onVestingCreated={() => {
          console.log('Vesting schedule created');
        }}
      />
    </PageShell>
  );
};

export default TokenLocksPage;
